import {
  Dispatch,
  Fragment,
  RefObject,
  SetStateAction,
  useRef,
  useState,
} from 'react';
import router from 'next/router';
import { Dialog, Transition } from '@headlessui/react';
import { SearchIcon } from 'lucide-react';
import SearchOutputMobile from './SearchOutput';
import { trackProductsSearched } from '@/lib/analytics/trackProduct';
import { SearchProductsResponseBody } from '@/api/catalog-v2/search/products';
import i18n from '@/settings/i18n';

const t = i18n.init('shared');

const SearchForm = ({
  search,
  setSearch,
  onSubmit,
  inputRef,
}: {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  onSubmit: () => void;
  inputRef: RefObject<HTMLInputElement>;
}) => (
  <form
    className="flex h-10 flex-1 items-center gap-2 rounded-full bg-gray-100 px-4 text-[13px]"
    onSubmit={(e) => {
      e.preventDefault();
      onSubmit();
    }}
  >
    <button
      type="submit"
      className="group flex h-full shrink-0 items-center justify-center"
      aria-label="Submit for search"
      id="e2e-searchbar-search-button"
    >
      <SearchIcon className="inline h-5 w-5 transition duration-300 ease-in-out group-hover:rotate-90" />
    </button>
    <input
      ref={inputRef}
      type="text"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder={t`Search by model, brand, color…`}
      aria-label="Search Input"
      className="h-full w-full border-none bg-transparent text-[13px] outline-none placeholder:text-gray-700/60"
      id="e2e-searchbar-search-input"
      autoComplete="off"
    />
  </form>
);

const MobileSearchModal = ({
  setShowSearchModal,
  showSearchModal,
  toggle,
  search,
  setSearch,
  onSearchNavigate,
}: {
  setShowSearchModal: Dispatch<SetStateAction<boolean>>;
  showSearchModal: boolean;
  toggle?: () => void;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  onSearchNavigate?: () => void;
}) => {
  const [results, setResults] = useState<SearchProductsResponseBody | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => setShowSearchModal(false);

  const onSubmit = async () => {
    const trimmedSearch = encodeURIComponent(search.trim());

    if (trimmedSearch.length === 0) {
      return;
    }

    if (results != null) {
      trackProductsSearched(search);
    }

    onSearchNavigate?.();
    toggle?.();
    close();

    await router.push(`/search?query=${trimmedSearch}`);
  };

  return (
    <Transition.Root show={showSearchModal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[70] sm:hidden"
        onClose={close}
        initialFocus={inputRef}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Panel className="fixed inset-0 overflow-y-auto bg-white">
            <div className="border-b border-gray-200">
              <div className="reebelo-container flex items-center gap-4 py-4">
                <SearchForm
                  search={search}
                  setSearch={setSearch}
                  onSubmit={onSubmit}
                  inputRef={inputRef}
                />
                <button
                  type="button"
                  onClick={close}
                  className="shrink-0 text-[13px] font-bold text-gray-700 underline underline-offset-4"
                  aria-label="Cancel search"
                >
                  {t`Cancel`}
                </button>
              </div>
            </div>
            <div className="reebelo-container pb-4">
              <SearchOutputMobile
                search={search}
                toggle={() => {
                  toggle?.();
                  setSearch(search);
                }}
                setResults={setResults}
                onClose={close}
                onAutofill={setSearch}
                onSearchNavigate={onSearchNavigate}
              />
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition.Root>
  );
};

export default MobileSearchModal;
